import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionStore } from '@/stores/useSessionStore.js';
import { useWorkbenchContextStore } from '@/stores/useWorkbenchContextStore.js';

/**
 * Prompt 14 Phase 3: URL Deep Link 표준
 * 표준 Query Parameter: sessionId, runId, messageId, evidenceId, vehicleId, timestamp
 * 기존 alias: messageEventId, report, testId, simulationTestId
 */
export interface DeepLinkContext {
  sessionId: string | null;
  runId: string | null;
  messageId: string | null;
  evidenceId: string | null;
  vehicleId: string | null;
  timestamp: string | null;
  // legacy aliases
  messageEventId: string | null;
  reportId: string | null;
  simulationTestId: string | null;
  scenarioId: string | null;
}

export interface DeepLinkParams {
  sessionId?: string | null;
  runId?: string | null;
  messageId?: string | null;
  evidenceId?: string | null;
  vehicleId?: string | null;
  timestamp?: string | null;
}

export const buildDeepLink = (path: string, params: DeepLinkParams): string => {
  const search = new URLSearchParams();
  const entries: Array<[string, string | null | undefined]> = [
    ['sessionId', params.sessionId],
    ['runId', params.runId],
    ['messageId', params.messageId],
    ['evidenceId', params.evidenceId],
    ['vehicleId', params.vehicleId],
    ['timestamp', params.timestamp],
  ];
  for (const [key, value] of entries) {
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

export const useDeepLinkContext = (): DeepLinkContext => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const loadSession = useSessionStore((s) => s.loadSession);
  const currentSession = useSessionStore((s) => s.currentSession);
  const selectSession = useWorkbenchContextStore((s) => s.selectSession);
  const selectRun = useWorkbenchContextStore((s) => s.selectRun);
  const selectMessage = useWorkbenchContextStore((s) => s.selectMessage);
  const selectEvidence = useWorkbenchContextStore((s) => s.selectEvidence);
  const selectVehicle = useWorkbenchContextStore((s) => s.selectVehicle);
  const selectTimestamp = useWorkbenchContextStore((s) => s.selectTimestamp);

  const context = useMemo<DeepLinkContext>(() => {
    const runId = searchParams.get('runId');
    const messageId = searchParams.get('messageId');
    const evidenceId = searchParams.get('evidenceId');
    const vehicleId = searchParams.get('vehicleId');
    const timestamp = searchParams.get('timestamp');
    const messageEventId = searchParams.get('messageEventId');
    const reportId = searchParams.get('reportId') ?? searchParams.get('report');
    const simulationTestId = searchParams.get('simulationTestId') ?? searchParams.get('testId');
    const scenarioId = searchParams.get('scenarioId');
    return {
      sessionId,
      runId,
      messageId: messageId ?? messageEventId,
      evidenceId,
      vehicleId,
      timestamp,
      messageEventId,
      reportId,
      simulationTestId,
      scenarioId,
    };
  }, [searchParams, sessionId]);

  useEffect(() => {
    if (sessionId && currentSession?.id !== sessionId) {
      loadSession(sessionId).catch((err) => {
        console.error('Failed to load session from URL:', err);
      });
    }
  }, [sessionId, currentSession?.id, loadSession]);

  useEffect(() => {
    if (context.sessionId) selectSession(context.sessionId);
    if (context.runId) selectRun(context.runId);
    if (context.messageId) selectMessage(context.messageId, context.timestamp);
    if (context.evidenceId) selectEvidence(context.evidenceId);
    if (context.vehicleId) selectVehicle(context.vehicleId);
    if (context.timestamp) selectTimestamp(context.timestamp);
  }, [
    context.sessionId,
    context.runId,
    context.messageId,
    context.evidenceId,
    context.vehicleId,
    context.timestamp,
    selectSession,
    selectRun,
    selectMessage,
    selectEvidence,
    selectVehicle,
    selectTimestamp,
  ]);

  return context;
};

export default useDeepLinkContext;
